import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import type { TypedRecordInstance } from '@warp-drive/core/types/record';
import type { WithLegacy } from '@warp-drive/legacy/model/migration-support';
import BsCollapse from 'ember-bootstrap/components/bs-collapse';

import { EditRecordBase } from './edit-record-base.gts';
import { ValidationMessage } from './validation.gts';

export class EditRecordCollapse<
  T extends WithLegacy<TypedRecordInstance>,
> extends EditRecordBase<
  T,
  {
    collapseLabel: string;
  }
> {
  @tracked collapsedByUser = true;

  toggle = () => {
    this.collapsedByUser = !this.collapsedByUser;
  };

  get isCollapsed(): boolean {
    return this.collapsedByUser && this.readonly;
  }

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <form {{on "submit" this.submitClicked}} ...attributes>
        <button
          type="button"
          class="btn btn-link p-0 rounded-2 w-100 text-start border-0 text-decoration-none
            {{unless this.readonly 'pe-none'}}"
          {{on "click" this.toggle}}
        >
          <div class="d-flex justify-content-between gap-2">
            <div class="flex-grow-1">
              <div class="gud-eyebrow text-dark">
                {{@collapseLabel}}

                {{#if this.readonly}}
                  {{#if this.isCollapsed}}
                    <iconify-icon inline icon="ph:caret-down" />
                  {{else}}
                    <iconify-icon inline icon="ph:caret-up" />
                  {{/if}}
                {{/if}}
              </div>
              {{#if (has-block "help")}}
                <div class="mt-1">
                  {{yield to="help"}}
                </div>
              {{/if}}
            </div>
          </div>
        </button>

        <fieldset disabled={{this.saving}}>
          {{#if this.readonly}}
            <ValidationMessage @validation={{this.validation}} />
          {{else}}
            <ValidationMessage @validation={{this.validation}} />
          {{/if}}
        </fieldset>

        <BsCollapse @collapsed={{this.isCollapsed}}>
          {{yield
            uniqueId
            this.isDisabled
            this.readonly
            this.validation
            to="form"
          }}
        </BsCollapse>

        <fieldset disabled={{this.saving}}>
          {{#if this.readonly}}
            <ValidationMessage @validation={{this.validation}} />

            <div class="d-flex justify-content-between gap-2">
              <button
                type="button"
                class="btn {{this.buttonSize}} text-nowrap btn-outline-primary"
                disabled={{@context.isEditing}}
                {{on "click" this.toggle}}
              >
                {{#if this.isCollapsed}}
                  Show Details
                  <iconify-icon inline icon="ph:caret-down" />
                {{else}}
                  Hide Details
                  <iconify-icon inline icon="ph:caret-up" />
                {{/if}}
              </button>

              <button
                type="button"
                class="btn btn-primary {{this.buttonSize}}"
                {{on "click" this.editClicked}}
                disabled={{@context.isEditing}}
              >{{@editLabel}}</button>
              {{yield uniqueId this.readonly this.validation to="buttons"}}
            </div>
          {{else}}
            <ValidationMessage @validation={{this.validation}} />

            {{#if (has-block "buttons")}}
              <div
                class="d-flex justify-content-between gap-2"
                {{this.interceptTransition}}
              >
                <div class="d-flex justify-content-center gap-2">
                  {{yield uniqueId this.readonly this.validation to="buttons"}}
                </div>
                <div class="d-flex justify-content-center gap-2">
                  <button
                    type="submit"
                    class="btn btn-primary {{this.buttonSize}}"
                  >{{this.saveLabel}}</button>
                  <button
                    type="button"
                    class="btn btn-outline-primary {{this.buttonSize}}"
                    {{on "click" this.cancelClicked}}
                  >{{this.cancelLabel}}</button>
                </div>
              </div>
            {{else}}
              <div
                class="d-flex justify-content-center gap-2"
                {{this.interceptTransition}}
              >
                <button
                  type="submit"
                  class="btn btn-primary {{this.buttonSize}}"
                >{{this.saveLabel}}</button>
                <button
                  type="button"
                  class="btn btn-outline-primary {{this.buttonSize}}"
                  {{on "click" this.cancelClicked}}
                >{{this.cancelLabel}}</button>
              </div>
            {{/if}}
          {{/if}}
        </fieldset>
      </form>
    {{/let}}
  </template>
}
